import type {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * The widget's model and maths, kept apart from its components on purpose: the
 * points calculation, the compiling form and the auto-compile apply all need
 * them, and importing them from `*-edit.ts` would pull the builder (and with it
 * the points model) back into the points calculation — an import cycle.
 */

/** The key of the built-in "nothing" option, always `options[0]`. */
export const DEFAULT_MULTIPLIER_OPTION_KEY = 'default';

export interface MultiplierOption {
  /** Stable id; the stored answer refers to it. `'default'` for the default option. */
  key: string;
  /** Shown to the applicant; also what the AI compile and the coherence check read. */
  label: string;
  /** >= 0, at most 3 decimals; always 0 on the default option. */
  multiplier: number;
  /** >= 0; absent on the default option. Required by the builder; read as 0 when missing. */
  min?: number;
  /** >= min; absent on the default option. Required by the builder. */
  max?: number;
  /** Only on `options[0]`. */
  isDefault?: true;
}

export interface MultiplierAnswer {
  /** A `MultiplierOption.key`. */
  option: string;
  /** 0 when `option` is the default; null only while the applicant has cleared the input. */
  value: number | null;
}

export interface MultiplierRadioButtonCustomization {
  description: string;
  required: boolean;
  showIfRule: string;
  autocompleteRule: string;
  /** False: integers only; true: up to 3 decimal places. */
  decimal: boolean;
  /** `options[0]` is the default option, always present. */
  options: MultiplierOption[];
  /** The saved answer, written in by the compiling dashboard (same idiom as MultiRadio). */
  defaultValue?: MultiplierAnswer;
}

/** An option as the builder form holds it, before it is saved: any field may be blank. */
export interface MultiplierOptionDraft {
  key?: string | null;
  multiplier?: number | null;
  max?: number | null;
  isDefault?: boolean | null;
}

/** Why an answer is not acceptable; see {@link multiplierAnswerError}. */
export type MultiplierAnswerError =
  | 'invalid'
  | 'unknown-option'
  | 'missing'
  | 'out-of-range'
  | 'not-integer';

/** The option every widget starts with: picking it means "no commitment", worth nothing. */
export function defaultMultiplierOption(label = 'Nessuno'): MultiplierOption {
  return {
    key: DEFAULT_MULTIPLIER_OPTION_KEY,
    label,
    multiplier: 0,
    isDefault: true,
  };
}

/** The untouched answer: the default option, value 0. */
export function emptyMultiplierAnswer(): MultiplierAnswer {
  return { option: DEFAULT_MULTIPLIER_OPTION_KEY, value: 0 };
}

/**
 * The default option carries both marks, `isDefault: true` and the key
 * `'default'` (and stands at `options[0]`, which only the builder can check).
 */
export function isDefaultMultiplierOption(
  option: MultiplierOptionDraft,
): boolean {
  return (
    option.isDefault === true && option.key === DEFAULT_MULTIPLIER_OPTION_KEY
  );
}

/**
 * The widget weight: the best score an option can reach, `multiplier × max`,
 * over the non-default options (0 when there are none). It stands where a
 * Radio's "max option weight" stands in the subsection's distribution.
 */
export function multiplierWidgetWeight(
  options: readonly MultiplierOptionDraft[] | null | undefined,
): number {
  return (options ?? []).reduce((best, option) => {
    if (isDefaultMultiplierOption(option)) return best;
    const reach = (option.multiplier ?? 0) * (option.max ?? 0);
    return reach > best ? reach : best;
  }, 0);
}

/** Whether a value has the shape of an answer. */
export function isMultiplierAnswer(value: unknown): value is MultiplierAnswer {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const { option, value: amount } = value as Record<string, unknown>;
  return (
    typeof option === 'string' &&
    option !== '' &&
    (amount === null || (typeof amount === 'number' && Number.isFinite(amount)))
  );
}

/**
 * The control's initial value: the saved answer when it is a usable one,
 * otherwise the default. A saved default pick is normalised to value 0.
 */
export function multiplierInitialValue(saved: unknown): MultiplierAnswer {
  if (!isMultiplierAnswer(saved)) return emptyMultiplierAnswer();
  if (saved.option === DEFAULT_MULTIPLIER_OPTION_KEY)
    return emptyMultiplierAnswer();
  return { option: saved.option, value: saved.value };
}

/**
 * The answer after the applicant picks `key`. The default (or a key the widget
 * does not have) forces the value to 0; any other option keeps the current
 * value clamped into its range, and an empty value becomes the option's min.
 */
export function selectMultiplierOption(
  options: readonly MultiplierOption[],
  key: string,
  current: number | null,
): MultiplierAnswer {
  const option = options.find((o) => o.key === key);
  if (!option || isDefaultMultiplierOption(option))
    return emptyMultiplierAnswer();

  const min = option.min ?? 0;
  if (current == null) return { option: option.key, value: min };

  const max = option.max ?? Number.POSITIVE_INFINITY;
  return { option: option.key, value: Math.min(Math.max(current, min), max) };
}

/**
 * The quantity as it is scored: floored on an integer-only widget, then
 * clamped into the option's `[min, max]` (a missing min reads as 0). An
 * option without a valid range (no max, or min > max) leaves the value as
 * stored; null stays null.
 */
export function multiplierScoredValue(
  option: MultiplierOption,
  value: number | null | undefined,
  decimal: boolean,
): number | null {
  if (value == null) return null;

  const min = option.min ?? 0;
  const max = option.max;
  if (max == null || min > max) return value;

  const stated = decimal ? value : Math.floor(value);
  return Math.min(Math.max(stated, min), max);
}

/**
 * Whether `value` is a quantity `option` accepts: present, within
 * `[min, max]`, and whole in integer mode. Null when it is.
 */
export function multiplierValueError(
  option: MultiplierOption,
  value: number | null | undefined,
  decimal: boolean,
): 'missing' | 'out-of-range' | 'not-integer' | null {
  if (value == null || !Number.isFinite(value)) return 'missing';
  if (
    value < (option.min ?? 0) ||
    value > (option.max ?? Number.POSITIVE_INFINITY)
  )
    return 'out-of-range';
  if (!decimal && !Number.isInteger(value)) return 'not-integer';
  return null;
}

/**
 * Checks an answer against the widget. The default option is always valid —
 * the compiling component keeps its value at 0 — so only a real pick can fail.
 */
export function multiplierAnswerError(
  answer: unknown,
  options: readonly MultiplierOption[],
  decimal: boolean,
): MultiplierAnswerError | null {
  if (!isMultiplierAnswer(answer)) return 'invalid';
  if (answer.option === DEFAULT_MULTIPLIER_OPTION_KEY) return null;

  const option = options.find((o) => o.key === answer.option);
  if (!option) return 'unknown-option';
  if (isDefaultMultiplierOption(option)) return null;

  return multiplierValueError(option, answer.value, decimal);
}

/** The control-level validator: `{ multiplierAnswer: <reason> }` when the answer fails. */
export function multiplierAnswerValidator(
  customization: Pick<
    MultiplierRadioButtonCustomization,
    'options' | 'decimal'
  >,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const reason = multiplierAnswerError(
      control.value,
      customization.options ?? [],
      !!customization.decimal,
    );
    return reason ? { multiplierAnswer: reason } : null;
  };
}
